// Persists pill selections across table re-renders (variantId → qty string)
const selectedQtyMap = new Map();

// Document-level delegation handles both static (cards) and dynamic (modal) pills
document.addEventListener('click', (e) => {
  // Trash (remove) button: clear selection and refresh all row prices
  const removeBtn = e.target.closest('.quick-order-list-remove-button');
  if (removeBtn) {
    const variantId = removeBtn.dataset.index;
    if (variantId) {
      selectedQtyMap.delete(variantId);
      const row = document.getElementById(`Variant-${variantId}`);
      if (row) {
        row.querySelectorAll('.qty-pill').forEach(p => p.classList.remove('selected'));
        const qol = row.closest('quick-order-list');
        if (qol) updateAllRowPrices(qol);
      }
    }
    return;
  }

  // Pill click
  const pill = e.target.closest('.qty-pill');
  if (!pill) return;
  const group = pill.closest('.qty-pills');
  if (!group) return;

  group.querySelectorAll('.qty-pill').forEach(p => p.classList.remove('selected'));
  pill.classList.add('selected');
  updateQty(group, pill.dataset.qty, true);
});

document.addEventListener('DOMContentLoaded', () => {
  // Initialize pre-selected pills on product cards
  document.querySelectorAll('.qty-pills').forEach(group => {
    const selected = group.querySelector('.qty-pill.selected');
    if (selected) updateQty(group, selected.dataset.qty, false);
  });

  observeForTables();

  // Re-apply pill state and discounted prices after every cart update re-renders sections.
  if (typeof subscribe === 'function') {
    subscribe('cart-update', () => {
      // Immediate pass: handles QOL-initiated updates (renderSections already done).
      document.querySelectorAll('.quick-order-list__table').forEach(restoreSelectedPills);
      // Refresh product card prices with the updated collection-total discount.
      refreshCardPrices();
      // Delayed pass: catches async re-renders triggered by foreign cart updates.
      setTimeout(() => {
        document.querySelectorAll('.quick-order-list__table').forEach(restoreSelectedPills);
        refreshCardPrices();
      }, 400);
    });
  }
});

function updateQty(group, qty, dispatchChange) {
  // Quick-order-list modal rows: hidden qty-pill-input inside .qty-selector
  const pillInput = group.closest('.qty-selector')?.querySelector('.qty-pill-input');
  if (pillInput) {
    selectedQtyMap.set(pillInput.dataset.index, qty);
    pillInput.value = qty;
    if (dispatchChange) {
      pillInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    // Refresh ALL rows using the new collection total (not just the clicked row)
    const quickOrderList = group.closest('quick-order-list');
    if (quickOrderList) {
      updateAllRowPrices(quickOrderList);
    }
    return;
  }

  // Product card (single-variant qty_pills): [name="quantity"] in product form
  const form = group.closest('form');
  if (form) {
    const input = form.querySelector('[name="quantity"]');
    if (input) input.value = qty;
    updatePrice(group, parseInt(qty, 10));
  }
}

// --- Restore selected pill state after quick-order-list re-renders the table ---

function observeForTables() {
  attachTableObservers();

  // Watch for tables that appear later when the modal opens
  new MutationObserver(() => attachTableObservers())
    .observe(document.body, { childList: true, subtree: true });
}

function attachTableObservers() {
  document.querySelectorAll('.quick-order-list__table').forEach(table => {
    if (table._pillsObserver) return; // already watching this table
    let debounceTimer = null;
    const obs = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => restoreSelectedPills(table), 30);
    });
    obs.observe(table, { childList: true, subtree: true });
    table._pillsObserver = obs;
    // Restore state immediately for newly discovered tables (e.g. popup reopened)
    setTimeout(() => restoreSelectedPills(table), 50);
  });
}

function restoreSelectedPills(table) {
  table.querySelectorAll('.qty-pills').forEach(group => {
    const pillInput = group.closest('.qty-selector')?.querySelector('.qty-pill-input');
    if (!pillInput) return;

    // If the server re-rendered this row with cart qty = 0, the item was removed.
    // Evict any stale map entry so the pill deselects and prices reset.
    const serverCartQty = parseInt(pillInput.dataset.cartQuantity || pillInput.value, 10);
    if (serverCartQty === 0) {
      selectedQtyMap.delete(pillInput.dataset.index);
    }

    let storedQty = selectedQtyMap.get(pillInput.dataset.index);
    // After page reload selectedQtyMap is empty — fall back to the cart qty
    // already baked into the input by Liquid and auto-populate the map.
    if (!storedQty) {
      const cartQty = pillInput.dataset.cartQuantity || pillInput.value;
      const match = [...group.querySelectorAll('.qty-pill')].find(p => p.dataset.qty === cartQty);
      if (match) {
        storedQty = cartQty;
        selectedQtyMap.set(pillInput.dataset.index, cartQty);
      }
    }
    if (!storedQty) return;
    group.querySelectorAll('.qty-pill').forEach(pill => {
      pill.classList.toggle('selected', pill.dataset.qty === storedQty);
    });
  });
  // Re-apply discounted prices — Liquid re-renders with original_line_price (undiscounted)
  const qol = table.closest('quick-order-list');
  if (qol) updateAllRowPrices(qol);
}

// --- Collection-total qty helpers ---

// Sum all selected qtys across every variant row in the quick-order-list,
// plus cart quantities from other products sharing the same discount group.
function getListTotalQty(quickOrderList) {
  let total = 0;
  const popupVariantIds = new Set();

  quickOrderList.querySelectorAll('.qty-pills').forEach(group => {
    const pillInput = group.closest('.qty-selector')?.querySelector('.qty-pill-input');
    if (!pillInput) return;
    popupVariantIds.add(String(pillInput.dataset.index));
    const stored = selectedQtyMap.get(pillInput.dataset.index);
    if (stored) total += parseInt(stored, 10);
  });

  // Add cart quantities for variants in the same discount group but not in this popup
  const firstGroup = quickOrderList.querySelector('.qty-pills[data-discount-group]');
  const discountGroup = firstGroup?.dataset.discountGroup;
  if (discountGroup) {
    try {
      const cartScript = document.getElementById('cart-discount-groups');
      if (cartScript) {
        JSON.parse(cartScript.textContent).forEach(item => {
          if (item.g === discountGroup && !popupVariantIds.has(String(item.v))) {
            total += item.q;
          }
        });
      }
    } catch (e) {}
  }

  return total;
}

// Compute the collection total once, then refresh every row with the shared discount %
function updateAllRowPrices(quickOrderList) {
  const collectionQty = getListTotalQty(quickOrderList);

  quickOrderList.querySelectorAll('.qty-pills').forEach(group => {
    const pillInput = group.closest('.qty-selector')?.querySelector('.qty-pill-input');
    if (!pillInput) return;
    const storedQty = selectedQtyMap.get(pillInput.dataset.index);
    const row = group.closest('tr');
    if (!row) return;

    if (!storedQty) {
      // No pill selected — reset row to zero state
      row.querySelectorAll('.variant-item__totals .price').forEach(el => {
        el.textContent = formatMoney(0);
      });
      row.querySelectorAll('[data-price-each]').forEach(el => {
        const originalPrice = parseInt(el.dataset.priceEach, 10);
        if (originalPrice) el.textContent = formatMoney(originalPrice) + '/ea';
      });
      return;
    }

    const qty = parseInt(storedQty, 10);
    const unitPrice = parseInt(group.dataset.unitPrice, 10);
    if (!unitPrice || !qty) return;

    let discountPercent = 0;
    try {
      const raw = group.dataset.qtyDiscount;
      if (raw) discountPercent = getDiscountPercent(JSON.parse(raw), collectionQty);
    } catch (e) {}

    const discountedUnit = Math.round(unitPrice * (1 - discountPercent / 100));
    row.querySelectorAll('.variant-item__totals .price').forEach(el => {
      el.textContent = formatMoney(discountedUnit * qty);
    });
    row.querySelectorAll('[data-price-each]').forEach(el => {
      el.textContent = formatMoney(discountedUnit) + '/ea';
    });
  });

  recalcSubtotal(quickOrderList);
}

// --- Price update (product cards only) ---

function getDiscountPercent(rules, qty) {
  if (!rules || !rules.length) return 0;
  const sorted = [...rules].sort((a, b) => b.min_qty - a.min_qty);
  for (const rule of sorted) {
    if (qty >= rule.min_qty) return rule.percent;
  }
  return 0;
}

function updatePrice(group, qty) {
  const unitPrice = parseInt(group.dataset.unitPrice, 10);
  if (!unitPrice || !qty) return;

  // Include cart quantities from the same discount group for the collection-total discount,
  // but exclude this card's own variant to avoid double-counting.
  let collectionQty = qty;
  const discountGroup = group.dataset.discountGroup;
  const variantId = group.dataset.variantId;
  if (discountGroup) {
    try {
      const cartScript = document.getElementById('cart-discount-groups');
      if (cartScript) {
        JSON.parse(cartScript.textContent).forEach(item => {
          if (item.g === discountGroup && String(item.v) !== String(variantId)) {
            collectionQty += item.q;
          }
        });
      }
    } catch (e) {}
  }

  let discountPercent = 0;
  try {
    const raw = group.dataset.qtyDiscount;
    if (raw) discountPercent = getDiscountPercent(JSON.parse(raw), collectionQty);
  } catch (e) {}

  const discountedUnit = Math.round(unitPrice * (1 - discountPercent / 100));
  const formattedTotal = formatMoney(discountedUnit * qty);

  // Product card context
  const card = group.closest('.card-wrapper');
  if (card) {
    card.querySelectorAll('.price__regular .price-item--regular').forEach(el => {
      el.textContent = formattedTotal;
    });
    card.querySelectorAll('.price__sale .price-item--sale').forEach(el => {
      el.textContent = formattedTotal;
    });
  }
}

// Sums discounted totals for all pill-selected variants using the collection total qty
function recalcSubtotal(quickOrderList) {
  const subtotalEl = quickOrderList.querySelector('.totals__subtotal-value');
  if (!subtotalEl) return;

  const collectionQty = getListTotalQty(quickOrderList);
  let totalCents = 0;

  quickOrderList.querySelectorAll('.qty-pills').forEach(group => {
    const pillInput = group.closest('.qty-selector')?.querySelector('.qty-pill-input');
    if (!pillInput) return;
    const storedQty = selectedQtyMap.get(pillInput.dataset.index);
    if (!storedQty) return;
    const qty = parseInt(storedQty, 10);
    if (!qty) return;
    const unitPrice = parseInt(group.dataset.unitPrice, 10);
    if (!unitPrice) return;

    let discountPercent = 0;
    try {
      const raw = group.dataset.qtyDiscount;
      if (raw) discountPercent = getDiscountPercent(JSON.parse(raw), collectionQty);
    } catch (e) {}

    totalCents += Math.round(unitPrice * (1 - discountPercent / 100)) * qty;
  });

  subtotalEl.textContent = formatMoney(totalCents);
}

function refreshCardPrices() {
  document.querySelectorAll('.qty-pills').forEach(group => {
    if (group.closest('.qty-selector')?.querySelector('.qty-pill-input')) return; // QOL row, skip
    const selected = group.querySelector('.qty-pill.selected');
    if (selected) updatePrice(group, parseInt(selected.dataset.qty, 10));
  });
}

function formatMoney(cents) {
  if (window.Shopify && window.Shopify.formatMoney) {
    return window.Shopify.formatMoney(cents);
  }
  const format = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
  const dollars = cents / 100;
  const amount = dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const amountNoDecimals = Math.round(dollars).toLocaleString('en-US');
  return format
    .replace('{{amount_with_comma_separators}}', amount)
    .replace('{{amount_no_decimals_with_comma_separators}}', amountNoDecimals)
    .replace('{{amount_no_decimals}}', amountNoDecimals)
    .replace('{{amount}}', amount);
}
